import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Chip, Grow, Paper } from '@mui/material';
import { Background } from '@/common/auth/components/layout/components/Background';
import { StudioVaultTitle } from '@/common/auth/components/layout/components/StudioVaultTitle';

const OAUTH_KEYS = [
  'access_token',
  'state',
  'error',
  'error_description',
  'expires_in',
] as const;

function parseOAuthParams(): URLSearchParams {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : '';
  const query = window.location.search.startsWith('?')
    ? window.location.search.slice(1)
    : '';
  return new URLSearchParams(hash || query);
}

function buildDeepLink(params: URLSearchParams): string {
  const url = new URL('studio-vault://auth/callback');
  OAUTH_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) url.searchParams.set(key, value);
  });
  return url.toString();
}

function buildLocalCallback(params: URLSearchParams): string {
  const url = new URL('http://127.0.0.1:8765/oauth-callback');
  OAUTH_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) url.searchParams.set(key, value);
  });
  return url.toString();
}

const DesktopAuthCallbackPage: React.FC = () => {
  const [statusText, setStatusText] = useState(
    'Finishing connection with Studio Vault...'
  );
  const deepLinkRef = useRef<string>('#');

  useEffect(() => {
    const params = parseOAuthParams();
    const deepLink = buildDeepLink(params);
    deepLinkRef.current = deepLink;

    if (!params.get('access_token') && !params.get('error')) {
      setStatusText(
        'Missing OAuth response from Google. Please try sign-in again.'
      );
      return;
    }

    // Attempt native deep-link handoff
    window.location.assign(deepLink);

    // Fallback: notify bridge server without navigating
    setTimeout(() => {
      fetch(buildLocalCallback(params), {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
      }).catch(() => {});
      setStatusText('If Studio Vault is not focused, click Open Studio Vault.');
    }, 700);
  }, []);

  const openStudioVault = () => {
    window.location.assign(deepLinkRef.current);
    window.close();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: 2,
        overflow: 'hidden',
      }}
    >
      <Background />

      <Grow in timeout={1000} style={{ transformOrigin: 'center' }}>
        <Box
          sx={{
            width: 'min(860px, calc(100vw - 32px))',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <StudioVaultTitle showSubtitle={false} sx={{ mb: 3 }} />

          <Paper
            variant="hero"
            sx={{
              p: 4,
              borderRadius: 4,
              backdropFilter: 'blur(20px)',
              width: 'min(560px, calc(100vw - 32px))',
            }}
          >
            {/* Kicker */}
            <Chip
              variant="soft"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      flexShrink: 0,
                    }}
                  />
                  Studio Vault Desktop Auth
                </Box>
              }
              size="small"
              sx={{
                height: 'auto',
                py: '6px',
                px: '10px',
                borderRadius: '999px',
                fontSize: '12px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                '& .MuiChip-label': { px: 0 },
              }}
            />

            <Typography
              variant="h4"
              sx={{ mt: 2, mb: 1, fontWeight: 700, letterSpacing: '-0.02em' }}
            >
              Sign-in approved
            </Typography>

            <Typography variant="body1" color="text.secondary">
              We sent your login back to the desktop app.
            </Typography>

            <Typography variant="body2" sx={{ mt: 1.5 }}>
              {statusText}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
              <Button
                variant="contained"
                onClick={openStudioVault}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Open Studio Vault
              </Button>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2 }}
            >
              You can close this tab once Studio Vault opens.
            </Typography>
          </Paper>
        </Box>
      </Grow>
    </Box>
  );
};

export default DesktopAuthCallbackPage;
